/**
 * ЗЕРКАЛО реестра пар имён полей блока — фронтовая копия
 * `backend/src/ai-funnels/block-schema/field-name-registry.ts`
 * (`FIELD_NAME_PAIRS_BY_BLOCK_TYPE`).
 *
 * ЗАЧЕМ ПАРЫ ВООБЩЕ ЕСТЬ. У части полей две номенклатуры: генерация пишет
 * короткое имя схемы (`accentColor`, `startLabel`, `steps`…), а кабинет —
 * префиксное (`chartAccentColor`, `chartStartLabel`, `loaderSteps`…).
 * Нормализация имён работает на ПРИЁМЕ генерации, а в базе лежат воронки,
 * записанные до неё, и миграции данных по решению WEB-880 нет. Поэтому
 * читатели (панель свойств и плеер) знают оба имени: имя редактора
 * приоритетнее, имя генерации — совместимость со старыми снапшотами.
 *
 * ЗАЧЕМ ЗЕРКАЛО ЗДЕСЬ (ревизия пачки 9, F5). Читать оба имени мало — надо ещё
 * уметь ЗАПИСАТЬ «значения нет». Панель пишет имя редактора, а `undefined`
 * гасит только его: под именем генерации старое значение остаётся жить и
 * тут же всплывает через фолбэк. У текстовых полей это не видно (они пишут
 * `''`, и `'' ?? short` возвращает пустую строку), а у цвета «Как в теме»
 * была МЕРТВА на всякой воронке, сгенерированной до канонизации: вернуть
 * наследование темы нельзя было ничем. Класс — не про цвет графика, а про
 * любое поле с двойным именем, поэтому запись чинится по реестру, а не
 * поштучно.
 *
 * Импортировать бэковый файл фронт не может (разные сборки и tsconfig), так
 * что копия неизбежна — и ровно поэтому рядом стоит сторож паритета
 * `blockFieldNamePairs.parity.test.ts`: он читает бэковый реестр С ДИСКА и
 * краснеет на любом расхождении (новая пара, переименование, удаление). Не
 * менять форму объявления ниже, не обновив сторож.
 *
 * Ключ — тип блока, дальше «имя генерации → имя редактора».
 */
export const BLOCK_FIELD_NAME_PAIRS: Readonly<
  Record<string, Readonly<Record<string, string>>>
> = {
  loader_processing: {
    title: 'loaderTitle',
    steps: 'loaderSteps',
    autoAdvance: 'loaderAutoAdvance',
    socialProof: 'loaderSocialProof',
    questions: 'loaderQuestions',
    skipLabel: 'loaderSkipLabel',
  },
  plan_selector: {
    plans: 'planSelectorPlans',
    layout: 'planSelectorLayout',
    defaultPriceId: 'planSelectorDefaultPriceId',
  },
  payment_terms: {
    template: 'paymentTermsTemplate',
    priceId: 'paymentTermsPriceId',
  },
  downsell_popup: {
    title: 'downsellTitle',
    text: 'downsellText',
    priceId: 'downsellPriceId',
    ctaLabel: 'downsellCtaLabel',
    triggers: 'downsellTriggers',
  },
  image_choice: {
    question: 'content',
    options: 'imageChoiceOptions',
    multiple: 'imageChoiceMultiple',
  },
  weight_picker: {
    question: 'content',
    unitDefault: 'weightPickerUnitDefault',
    min: 'weightPickerMin',
    max: 'weightPickerMax',
  },
  height_picker: {
    question: 'content',
    unitDefault: 'heightPickerUnitDefault',
    min: 'heightPickerMin',
    max: 'heightPickerMax',
  },
  cookie_consent: {
    text: 'cookieConsentText',
    acceptLabel: 'cookieAcceptLabel',
    declineLabel: 'cookieDeclineLabel',
    policyUrl: 'cookiePolicyUrl',
    position: 'cookiePosition',
  },
  chart: {
    kind: 'chartKind',
    startLabel: 'chartStartLabel',
    endLabel: 'chartEndLabel',
    startValue: 'chartStartValue',
    endValue: 'chartEndValue',
    accentColor: 'chartAccentColor',
    // WEB-3252: столбцы вида «Столбцы».
    bars: 'chartBars',
  },
  photo_upload: {
    question: 'photoUploadQuestion',
    maxSizeMb: 'photoUploadMaxSizeMb',
    accept: 'photoUploadAccept',
  },
};

/**
 * ЗЕРКАЛО ВТОРОГО СПИСКА ТОГО ЖЕ РЕЕСТРА — `NOT_RENAMED_ON_INTAKE`
 * (`backend/src/ai-funnels/block-schema/field-name-registry.ts`): имена
 * генерации, которые нормализация на приёме сознательно НЕ переписывает.
 *
 * ЗАЧЕМ ЭТО ЗНАТЬ ФРОНТУ (находка Б1 ревизии волны хвостов-8). Такое имя —
 * не «мусор из прошлого формата», а ЖИВОЙ адрес поля: по имени `options`
 * картиночный выбор адресуют три generic-обходчика содержимого бэка —
 * `persistent-id/remap-ids.ts` (выдача и переписывание id вариантов),
 * `resolve-rule-refs.util.ts` (резолв «текст ответа → option.id»),
 * `funnel-graph-validation.ts` (per-option `nextScreenId`). Погасить его —
 * значит молча вывести блок из-под всех трёх и стереть список вариантов
 * покупателя вместе с ветвлением: у ИИ-воронки он лежит ровно там.
 *
 * Поэтому гашение (`withPairedGenerationNamesCleared`) эти имена пропускает,
 * а читатели обязаны знать оба имени — панель и плеер читают
 * `imageChoiceOptions ?? options`. Для СПИСКОВ это безопасно и без гашения:
 * пустой список пишется как `[]`, а `[] ?? options` — это `[]`, то есть
 * «вариантов нет» доезжает до читателя честно (в отличие от скалярного
 * `undefined`, ради которого гашение и заводилось).
 *
 * Список под тем же сторожем паритета, что и сам реестр пар.
 */
export const GENERATION_NAMES_KEPT_ON_INTAKE: Readonly<
  Record<string, readonly string[]>
> = {
  // WEB-1690: список опустел синхронно с бэком. Обходчики перешли на резолвер
  // живого списка (оба имени из реестра), а владелец 26.08 ратифицировал
  // канонизацию: миграция 221 переносит `options` → `imageChoiceOptions` во
  // всех воронках, и имя генерации у картиночного выбора снова стало тем, чем
  // оно является у остальных пар, — наследием прошлого формата, которое
  // гашение при правке имеет право снимать. Сторож паритета сверяет этот
  // список с бэковым `NOT_RENAMED_ON_INTAKE` с диска — менять только парой.
};

/**
 * Обратный поиск: по типу блока и ИМЕНИ РЕДАКТОРА вернуть парное ИМЯ
 * ГЕНЕРАЦИИ. Пары нет — `undefined` (реестр не выдумывает имён; у поля без
 * пары гасить нечего).
 */
export function generationFieldName(
  blockType: string | undefined,
  editorName: string,
): string | undefined {
  if (!blockType) return undefined;
  const pairs = BLOCK_FIELD_NAME_PAIRS[blockType];
  if (!pairs) return undefined;
  for (const [generationName, knownEditorName] of Object.entries(pairs)) {
    if (knownEditorName === editorName) return generationName;
  }
  return undefined;
}

/**
 * ЧТЕНИЕ поля блока с учётом пары — фронтовый близнец бэкового
 * `readBlockField` (`field-name-registry.ts`). Адресуется ИМЕНЕМ РЕДАКТОРА
 * (панель мыслит ими), возвращается первое непустое: имя редактора
 * приоритетно, имя генерации — совместимость со старыми снапшотами.
 *
 * WEB-1782. До этой функции панель знала пары ТОЧЕЧНО: график через
 * самодельный `generatedChartFields`, тогл автоперехода и варианты
 * картиночного выбора — ручными `?? block.<короткое>`. Остальные ~27 пар были
 * слепы: у воронки, сгенерированной до канонизации (WEB-880, 2026-08-01),
 * на экране текст есть, а поле панели ПУСТОЕ — автор правил «пустое» и
 * сгенерированный текст исчезал из показа. Три способа делать одно и то же
 * гарантировали, что четвёртое поле опять забудут, поэтому чтение сведено
 * сюда, к реестру: новая пара в реестре — и читатель уже умеет её читать.
 *
 * У поля без пары возвращается просто его значение — функция безопасна как
 * замена прямого `block.<имя>` в любой ветке панели.
 *
 * Бэкфилла данных нет и не будет («живые воронки не трогаем»), поэтому
 * фолбэк здесь — не временная мера, а постоянный контракт чтения.
 */
export function readPairedField<T = unknown>(
  block: unknown,
  editorName: string,
): T | undefined {
  // `unknown` на входе — намеренно, как у бэкового близнеца: тип `EditorBlock`
  // знает ТОЛЬКО имена редактора, а имя генерации у старой воронки лежит вне
  // этого типа. Требовать `Record<string, unknown>` значило бы приводить блок
  // в каждой из трёх десятков веток панели.
  if (!block || typeof block !== 'object') return undefined;
  const record = block as Record<string, unknown>;
  const own = record[editorName];
  // Ревизия волны (находка 8): null НЕ останавливает фолбэк — заменённые
  // `?? `-цепочки на null шли дальше к имени генерации, и модель вполне
  // отдаёт null в JSON. Сужение семантики в 35 местах разом — молча.
  if (own !== undefined && own !== null) return own as T;
  const blockType = typeof record.type === 'string' ? record.type : undefined;
  const generationName = generationFieldName(blockType, editorName);
  if (!generationName || generationName === editorName) return undefined;
  return record[generationName] as T | undefined;
}

/**
 * Дополняет правку панели гашением ПАРНЫХ имён генерации.
 *
 * Правило: раз автор задал поле под именем редактора, второе имя того же
 * поля — мусор из прошлого формата, и держать его нельзя ни при каком
 * значении. Иначе сброс в «как в теме» (`undefined`) гасит только половину
 * пары, а вторая половина немедленно всплывает через фолбэк читателей.
 *
 * Гасим ТОЛЬКО когда старое имя реально лежит в блоке: у новых воронок его
 * нет, и подмешивать в правку лишние ключи незачем. Если имя генерации уже
 * пришло в самой правке — не трогаем, автор правки знает лучше.
 *
 * И НИКОГДА не гасим имена из `GENERATION_NAMES_KEPT_ON_INTAKE`: пока имя
 * там — оно не наследие, а живой адрес поля для чужих читателей. На этом
 * правиле держалась находка Б1: гашение `options` картиночного выбора,
 * бывшего тогда адресом generic-обходчиков бэка, стирало список вариантов
 * покупателя первой же правкой панели. После WEB-1690 (обходчики на
 * резолвере, канонизация миграцией 221) список пуст и ветка спит — но
 * механизм остаётся: следующая пара с «удерживаемым» именем обязана попасть
 * под ту же защиту, а не повторить Б1.
 */
export function withPairedGenerationNamesCleared<
  T extends Record<string, unknown>,
>(block: Record<string, unknown> | undefined, updates: T): T {
  if (!block) return updates;
  const blockType = typeof block.type === 'string' ? block.type : undefined;
  if (!blockType || !BLOCK_FIELD_NAME_PAIRS[blockType]) return updates;

  const keptNames = GENERATION_NAMES_KEPT_ON_INTAKE[blockType] ?? [];

  let extra: Record<string, undefined> | null = null;
  for (const editorName of Object.keys(updates)) {
    const generationName = generationFieldName(blockType, editorName);
    if (!generationName) continue;
    if (generationName === editorName) continue;
    if (keptNames.includes(generationName)) continue;
    if (generationName in updates) continue;
    if (!Object.prototype.hasOwnProperty.call(block, generationName)) continue;
    extra = extra ?? {};
    extra[generationName] = undefined;
  }
  return extra ? ({ ...updates, ...extra } as T) : updates;
}

/** Имя поля со списком вариантов у генерации. */
const BLOCK_OPTIONS_FIELD = 'options';

/**
 * Живой список вариантов ответа блока — зеркало бэкового
 * `resolveBlockOptions` (WEB-1690): имя редактора из реестра в приоритете,
 * иначе имя генерации `options`, иначе пусто. Читатели берут его по
 * прежнему адресу `resolveEditorBlockOptions.ts`; почему тело здесь, а не
 * там (вес первого экрана, WEB-3764), — в шапке того файла.
 */
export function resolveEditorBlockOptions(block: unknown): unknown[] {
  if (typeof block !== 'object' || block === null || Array.isArray(block)) {
    return [];
  }
  const record = block as Record<string, unknown>;
  const editorName =
    typeof record.type === 'string'
      ? (BLOCK_FIELD_NAME_PAIRS[record.type]?.[BLOCK_OPTIONS_FIELD] ??
        BLOCK_OPTIONS_FIELD)
      : BLOCK_OPTIONS_FIELD;
  const live = record[editorName];
  if (Array.isArray(live)) return live;
  const generated = record[BLOCK_OPTIONS_FIELD];
  return Array.isArray(generated) ? generated : [];
}
